import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link } from "react-router-dom";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";

type Props = {
  jobId: number;
  autoGenerateEnabled: boolean;
  onCancel: () => void;
};

function RunPlatoNoCandidatesModal({ jobId, autoGenerateEnabled, onCancel }: Props) {
  return (
    <CenterModal headerTitleText="No candidates yet" onCancel={onCancel}>
      <Styled.Body>
        There are no candidates to score yet. When there are, running Plato scores everyone in
        this job in bulk.
      </Styled.Body>

      {autoGenerateEnabled ? (
        <React.Fragment>
          <Styled.Body>
            No need to worry, though. Automatic review is on, so Plato will score every applicant
            for this job as they apply.
          </Styled.Body>
          <Styled.Lead>One thing to double-check:</Styled.Lead>
          <Styled.TaskBox>
            <Styled.Item>
              <Icon name="file-text" />
              <span>
                <Styled.ItemTitle>
                  Write a specific job description for best scoring results
                </Styled.ItemTitle>
                <Styled.ItemText>
                  Plato scores candidates against the requirements you set, so a clear, specific
                  description with well-defined requirements means more precise fit scores.
                </Styled.ItemText>
              </span>
            </Styled.Item>
          </Styled.TaskBox>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Styled.Lead>Two things get Plato ready:</Styled.Lead>
          <Styled.Steps>
            <Styled.Item>
              <Icon name="sliders" />
              <span>
                <Styled.ItemTitle>Turn on automatic review (optional)</Styled.ItemTitle>
                <Styled.ItemText>
                  Have Plato score each new applicant as they apply. Turn this on for your whole
                  organization in{" "}
                  <Styled.InlineLink to="/hire/settings/plato-ai/settings" onClick={onCancel}>
                    Plato organization settings
                  </Styled.InlineLink>
                  , or just for this job in its{" "}
                  <Styled.InlineLink to={`/jobs/${jobId}/setup/ai`} onClick={onCancel}>
                    Plato AI settings
                  </Styled.InlineLink>.
                </Styled.ItemText>
              </span>
            </Styled.Item>
            <Styled.Item>
              <Icon name="file-text" />
              <span>
                <Styled.ItemTitle>
                  Write a specific job description for best scoring results
                </Styled.ItemTitle>
                <Styled.ItemText>
                  Plato scores candidates against the requirements you set, so a clear, specific
                  description with well-defined requirements means more precise fit scores.
                </Styled.ItemText>
              </span>
            </Styled.Item>
          </Styled.Steps>
        </React.Fragment>
      )}

      <Styled.Actions>
        <Button type="internalLink" styleType="primary" link={`/jobs/${jobId}/setup/description`} onClick={onCancel}>
          Edit description
        </Button>
        <Button type="button" styleType="secondary" onClick={onCancel}>
          Dismiss
        </Button>
      </Styled.Actions>
    </CenterModal>
  );
}

RunPlatoNoCandidatesModal.propTypes = {};
RunPlatoNoCandidatesModal.defaultProps = {};

export default RunPlatoNoCandidatesModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Body = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_Body;
    ${t.mt(3)}
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    line-height: 1.5;
  `;
});

Styled.Lead = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_Lead;
    ${t.mt(3)}
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    line-height: 1.5;
  `;
});

Styled.Steps = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_Steps;
    ${t.mt(2)}
    display: flex;
    flex-direction: column;
  `;
});

Styled.TaskBox = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_TaskBox;
    ${[t.mt(3), t.p(4), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  `;
});

Styled.Item = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_Item;
    display: flex;
    gap: 0.8125rem;
    padding: 0.875rem 0;

    ${Styled.Steps} & + & {
      border-top: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    }
    ${Styled.TaskBox} & {
      padding: 0;
    }

    > svg {
      flex-shrink: 0;
      margin-top: 0.0625rem;
      width: 1rem;
      height: 1rem;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    }

    > span {
      display: flex;
      flex-direction: column;
      gap: 0.1875rem;
    }
  `;
});

Styled.ItemTitle = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_ItemTitle;
    ${t.text.sm}
    font-weight: 600;
    color: ${t.dark ? t.color.gray[100] : t.color.gray[900]};
  `;
});

Styled.ItemText = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_ItemText;
    font-size: 0.78125rem;
    line-height: 1.45;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
  `;
});

Styled.InlineLink = styled(Link)((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_InlineLink;
    color: ${t.dark ? t.color.white : t.color.black};
    font-weight: 600;
    text-decoration: underline !important;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
      color: ${t.dark ? t.color.gray[200] : t.color.gray[800]};
    }
  `;
});

Styled.Actions = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: RunPlatoNoCandidatesModal_Actions;
    ${t.mt(5)}
    display: flex;
    gap: 0.625rem;
  `;
});
